const { debounce } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.changeLineItem = this.changeLineItem.bind(this)
    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    if (this.els?.remove?.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    if (this.els?.variant && this.els?.variant.exists) {
      this.listeners.add(
        this.els.variant.element,
        'change',
        this.changeLineItem,
      )
    }
  }

  // for testing
  async getCartInfo () {
    try {
      const response = await fetch('/cart.js')
      const cart = await response.json()

      const cartTotal = cart.total_price
      const cartItems = cart.items

      return { cartTotal, cartItems }
    } catch (error) {
      console.error('Error fetching cart total:', error)
      return { cartTotal: null, cartItems: null }
    }
  }

  async removeLineItem (e, { silent = false } = {}) {
    try {
      e?.preventDefault()

      this.loading = true

      const state = await window.BAO.Cart.removeItem(
        this.dataset.key,
        {
          sections: this.mappedSections,
        },
      )

      console.info({
        __: 'removeLineItem',
        cartInfo: await this.getCartInfo(),
        datasetKey: this.dataset.key,
        state,
      })

      this.loading = false

      if (!silent) {
        if ('sections' in state) {
          this.renderSections(state.sections)
        }
      }

      return state
    } catch (error) {
      console.error('Error in removeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
      })
      this.loading = false
    }
  }

  async changeLineItem (e) {
    try {
      this.loading = true

      const newVariantId = e.target.value

      if (e.target.dataset?.gwp === 'true') {
        const gwpDataElement = this.externalEls.gwp.element
        if (gwpDataElement) {
          gwpDataElement.dataset.variantId = newVariantId
        }
      }

      // Store the original key before any changes
      const originalKey = this.dataset.key

      // Get the current quantity from the cart, not from the select element
      const cartState = await window.BAO.Cart.getCart()
      const currentItem = cartState.items.find(item => item.key === originalKey)
      const currentQuantity = currentItem?.quantity ?? 1
      const maxQuantity = 999
      const quantity = Math.min(currentQuantity, maxQuantity)
      const originalVariantId = currentItem.variant_id

      console.info({
        __: 'changeLineItem before removing old variant',
        cartInfo: await this.getCartInfo(),
        originalKey,
        originalVariantId,
        currentQuantity,
        currentItem,
      })

      // Step 1: Remove the old variant FIRST (before adding new one)
      // Find ALL items with this variant ID
      const itemsToRemove = cartState.items.filter(i => i.variant_id === originalVariantId)

      console.info({
        __: 'About to remove old variant',
        originalKey,
        originalVariantId,
        itemsToRemove: itemsToRemove.map(i => ({ key: i.key, id: i.variant_id, qty: i.quantity })),
      })

      // Remove ALL items with the original variant ID
      let state = cartState
      if (itemsToRemove.length > 0) {
        for (const item of itemsToRemove) {
          state = await window.BAO.Cart.cartChange({
            id: item.key,
            quantity: 0,
            sections: this.mappedSections,
            sections_url: window.location.pathname,
          })
        }

        console.info({
          __: 'changeLineItem after removing old variant',
          stateItems: state.items.map(i => ({ key: i.key, id: i.variant_id, qty: i.quantity })),
          originalVariantIdRemoved: originalVariantId,
          removalSuccess: !state.items.find(i => i.variant_id === originalVariantId),
        })
      }

      // Step 2: Add the new variant
      if (e.target.dataset?.gwp !== 'true') {
        state = await window.BAO.Cart.addItem(
          { id: newVariantId, quantity },
          {
            sections: this.mappedSections,
            silent: true,
          },
        )
      }

      console.info({
        __: 'changeLineItem after adding new variant',
        cartInfo: await this.getCartInfo(),
        stateItems: state.items.map(i => ({ key: i.key, id: i.variant_id, qty: i.quantity })),
      })

      const pushCart = this.pushCart

      if ('sections' in state) {
        this.renderSections(state.sections)
      }

      if (pushCart) {
        pushCart.loading = false
      }

      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (error) {
      console.error('Error in changeLineItem:', {
        message: error.message,
        stack: error.stack,
        datasetKey: this.dataset.key,
        newVariantId: e.target.value,
      })

      const pushCart = this.pushCart
      if (pushCart) {
        pushCart.loading = false
      }
    } finally {
      console.info({
        __: 'changeLineItem finally',
        cartInfo: await this.getCartInfo(),
      })
    }
  }

  async onQuantityChange (e) {
    try {
      this.loading = true

      const state = await window.BAO.Cart.updateQuantity(
        this.dataset.key,
        e.detail.value,
        { sections: this.mappedSections },
      )

      this.loading = false

      if ('sections' in state) this.renderSections(state.sections)
    } catch (error) {
      console.error('Error in onQuantityChange:', {
        message: error.message,
        stack: error.stack,
        quantity: e.detail.value,
        datasetKey: this.dataset.key,
        value: e.detail.value,
        sections: this.mappedSections,
      })
      this.loading = false
    }
  }

  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
